import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TabViewModule } from 'primeng/tabview';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DropdownModule } from 'primeng/dropdown';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CalendarOptions } from '@fullcalendar/core';
import { FullCalendarModule } from '@fullcalendar/angular';
import dayGridPlugin from '@fullcalendar/daygrid';
import { forkJoin } from 'rxjs';
import moment from 'moment';

import { PropertyService } from '../properties/service/property.service';
import { BlackoutService } from '../properties/service/blackout.service';
import { AvailabilityApiService } from '../properties/availability/availability.service';

@Component({
  selector: 'app-availability-reports',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    TabViewModule, CardModule, ButtonModule, TableModule,
    TagModule, TooltipModule, DropdownModule, ProgressSpinnerModule,
    FullCalendarModule,
  ],
  templateUrl: './availability-reports.component.html',
  styleUrl: './availability-reports.component.scss',
})
export class AvailabilityReportsComponent implements OnInit, OnDestroy {

  propertyId = '';

  weekStart = moment().startOf('isoWeek');
  weekEnd   = moment().endOf('isoWeek');
  loading      = false;
  openDays      = 0;
  closedDays    = 0;
  totalBookings = 0;
  residents     = 0;
  weekRows: any[] = [];
  expandedRows: Record<string, boolean> = {};

  tenants: { label: string; value: string }[] = [];
  selectedTenantId: string | null = null;
  selectedTenantName = '';
  blackoutLoading = false;
  fullDayCount  = 0;
  partialCount  = 0;

  private tooltipEl: HTMLElement | null = null;

  blackoutCalendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin],
    initialView: 'dayGridMonth',
    headerToolbar: { left: 'prev,next', center: 'title', right: '' },
    events: [],
    eventContent: (arg) => {
      if (arg.event.extendedProps['isPartial']) {
        return { html: `<span class="partial-label">${arg.event.title}</span>` };
      }
      return { html: '' };
    },
    eventMouseEnter: (info) => {
      const props = info.event.extendedProps;
      const text = props['reasonText'] || props['reasonType'];
      if (text) this.showTooltip(info.jsEvent as MouseEvent, text);
    },
    eventMouseLeave: () => this.hideTooltip(),
  };

  constructor(
    private propertyService: PropertyService,
    private blackoutService: BlackoutService,
    private availabilityService: AvailabilityApiService,
  ) {}

  ngOnInit(): void {
    this.propertyService.getUserInfo().subscribe(user => {
      this.propertyId = user.property_id;
      this.load();
      this.loadTenants();
    });
  }


  get weekLabel(): string {
    return `${this.weekStart.format('MMM D')} – ${this.weekEnd.format('MMM D, YYYY')}`;
  }

  prevWeek(): void {
    this.weekStart = this.weekStart.clone().subtract(1, 'week');
    this.weekEnd   = this.weekEnd.clone().subtract(1, 'week');
    this.load();
  }

  nextWeek(): void {
    this.weekStart = this.weekStart.clone().add(1, 'week');
    this.weekEnd   = this.weekEnd.clone().add(1, 'week');
    this.load();
  }

  load(): void {
    this.loading = true;
    const startDate = this.weekStart.format('YYYY-MM-DD');
    const endDate   = this.weekEnd.format('YYYY-MM-DD');
    forkJoin({
      summary: this.loadSummaryData(startDate, endDate),
      table:   this.propertyService.getWeekAvailabilityReport(this.propertyId, startDate, endDate),
    }).subscribe({
      next: ({ summary, table }) => {
        this.computeSummary(summary.availability, summary.blackouts, summary.tours?.data ?? []);
        this.weekRows = table;
        this.loading  = false;
      },
      error: () => { this.loading = false; },
    });
  }

  private loadSummaryData(startDate: string, endDate: string) {
    return forkJoin({
      availability: this.availabilityService.get(this.propertyId),
      blackouts:    this.blackoutService.getBlackouts(this.propertyId),
      tours:        this.propertyService.getAllFollowups({
        page: 1, limit: 1000,
        filters: JSON.stringify({ appointmentDate: [moment(startDate).toDate(), moment(endDate).toDate()] }),
      }),
    });
  }

  private computeSummary(availability: any, blackouts: any[], tours: any[]): void {
    const days: string[] = [];
    const cur = this.weekStart.clone();
    while (cur.isSameOrBefore(this.weekEnd, 'day')) {
      days.push(cur.format('MM/DD/YYYY'));
      cur.add(1, 'day');
    }
    const blackoutDates = new Set(blackouts.filter(b => !b.userId && b.isFullDay).map(b => b.date));
    const weeklySlots   = availability?.weeklySlots ?? {};
    let openDays = 0, closedDays = 0;
    days.forEach(date => {
      const key     = moment(date, 'MM/DD/YYYY').format('dddd').toLowerCase();
      const hasSlots = (weeklySlots[key] ?? []).length > 0;
      hasSlots && !blackoutDates.has(date) ? openDays++ : closedDays++;
    });
    this.openDays   = openDays;
    this.closedDays = closedDays;
    const weekTours = tours.filter(t => {
      const d = moment(t.date, 'MM/DD/YYYY');
      return d.isSameOrAfter(this.weekStart, 'day') &&
             d.isSameOrBefore(this.weekEnd, 'day') &&
             t.tourStatus !== 'cancelled';
    });
    this.totalBookings = weekTours.length;
    this.residents = new Set(weekTours.map(t => t.user_id?.toString()).filter(Boolean)).size;
  }


  private loadTenants(): void {
    this.propertyService.getTenants({ page: 1, limit: 200 }).subscribe({
      next: (res: any) => {
        this.tenants = (res.data ?? []).map((t: any) => ({
          label: `${t.first_name} ${t.last_name}`,
          value: t._id,
        }));
      },
    });
  }

  onTenantSelect(): void {
    if (!this.selectedTenantId) return;
    const found = this.tenants.find(t => t.value === this.selectedTenantId);
    this.selectedTenantName = found?.label ?? '';
    this.loadTenantBlackouts();
  }

  private loadTenantBlackouts(): void {
    this.blackoutLoading = true;
    this.blackoutService.getTenantBlackouts(this.propertyId, this.selectedTenantId!).subscribe({
      next: (blackouts) => {
        this.fullDayCount = blackouts.filter(b => b.isFullDay).length;
        // Count unique dates with partial slots only dates are exclude that also have a full-day blackout
        const fullDayDates = new Set(blackouts.filter(b => b.isFullDay).map(b => b.date));
        this.partialCount = new Set(
          blackouts.filter(b => !b.isFullDay && !fullDayDates.has(b.date)).map(b => b.date)
        ).size;
        this.buildBlackoutCalendar(blackouts);
        this.blackoutLoading = false;
      },
      error: () => { this.blackoutLoading = false; },
    });
  }

  private buildBlackoutCalendar(blackouts: any[]): void {
    // Group by date; full-day takes priority over partials on the same date
    const dateMap: Record<string, any[]> = {};
    blackouts.forEach(b => {
      if (!dateMap[b.date]) dateMap[b.date] = [];
      dateMap[b.date].push(b);
    });

    const events: any[] = [];
    Object.entries(dateMap).forEach(([date, list]) => {
      const [m, d, y] = date.split('/').map(Number);
      const fullDay = list.find(b => b.isFullDay);

      if (fullDay) {
        events.push({
          start:   new Date(Date.UTC(y, m - 1, d)),
          end:     new Date(Date.UTC(y, m - 1, d + 1)),
          allDay:  true,
          display: 'background',
          classNames: ['res-full-blackout'],
          extendedProps: {
            isPartial:  false,
            reasonText: fullDay.reasonText || '',
            reasonType: fullDay.reasonType || '',
          },
        });
      } else {
        list.forEach(b => {
          const title = `${this.fmt(b.startTime)}–${this.fmt(b.endTime)}`;
          events.push({
            id:      b._id,
            title,
            // Use date-only UTC to avoid time-zone rollover to next/prev day
            start:   new Date(Date.UTC(y, m - 1, d)),
            end:     new Date(Date.UTC(y, m - 1, d + 1)),
            allDay:  true,
            classNames: ['res-partial-blackout'],
            extendedProps: {
              isPartial:  true,
              reasonText: b.reasonText || '',
              reasonType: b.reasonType || '',
            },
          });
        });
      }
    });

    this.blackoutCalendarOptions = { ...this.blackoutCalendarOptions, events };
  }

  // ── Shared helpers ────────────────────────────────────────────────────────

  formatSlot(slot: any): string {
    return `${this.fmt(slot.start)} – ${this.fmt(slot.end)}`;
  }

  private fmt(t: string): string {
    return moment(t, 'HH:mm').format('h:mm A');
  }

  formatDate(date: string): string {
    return moment(date, 'MM/DD/YYYY').format('MMM D, YYYY');
  }

  formatTourTime(time: string): string {
    return moment(time, 'HH:mm').format('h:mm A');
  }

  slotTooltip(slots: any[]): string {
    return slots.map(s => this.formatSlot(s)).join('\n');
  }

  statusSeverity(status: string): 'success' | 'danger' {
    return status === 'open' ? 'success' : 'danger';
  }

  statusLabel(status: string): string {
    return status === 'open' ? 'Open' : 'Closed';
  }

  ngOnDestroy(): void {
    this.hideTooltip();
  }

  private showTooltip(e: MouseEvent, text: string): void {
    this.hideTooltip();
    
    const el = document.createElement('div');
    el.textContent = text;
    el.classList.add('custom-tooltip');
    
    document.body.appendChild(el);
    this.tooltipEl = el;
    el.style.position = 'fixed';
    el.style.left = (e.clientX + 14) + 'px';
    el.style.top = (e.clientY + 14) + 'px';
  }

  private hideTooltip(): void {
    this.tooltipEl?.remove();
    this.tooltipEl = null;
  }
}
